<template>
  <div class="page-notifications flex flex-col"
       :class="{
        'h-[calc(100vh-144px)]': isEcosystemMember,
        'h-[calc(100vh-75px)]': !isEcosystemMember
      }"
  >
    <!-- Fixed Header -->
    <div class="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4 z-10 relative">
      <div class="tw-flex items-center justify-between">
        <div class="tw-flex items-center space-x-4">
          <h1 class="text-2xl font-bold text-gray-900">{{ $t('generic_notifications') }}</h1>
          <badge v-if="unreadNotificationsBadgeCount > 0" :name="unreadNotificationsBadgeCount + ' unread'" variant="primary"/>
        </div>

        <div class="tw-flex items-center space-x-3">
          <ds-button
            variant="outline"
            icon="settings"
            @click="toggleNotificationSettings"
            v-if="shouldShowSettings"
            :class="{ 'bg-gray-100': showSettingsPanel }"
          />
          <ds-button variant="primary" icon="check" label="Mark All Read" @click="setReadAllNotifications()"
                     v-if="isLoggedIn && unreadNotificationsBadgeCount > 0"/>
        </div>
      </div>
    </div>

    <!-- Content - Takes remaining space -->
    <div class="flex-1 tw-flex flex-row w-full overflow-hidden">
      <!-- Notifications List -->
      <div :class="showSettingsPanel ? 'w-1/2' : 'w-full'" class="overflow-y-auto bg-gray-50 transition-all duration-300" ref="scrollable">
        <div class="p-6">
          <!-- Featured notification -->
          <div v-if="topNotification && topNotification.type" class="mb-6">
            <div class="mb-3 tw-flex items-center">
              <div class="w-2 h-2 bg-primary-community rounded-full mr-2"></div>
              <span class="text-sm font-medium text-primary-community uppercase tracking-wide">Featured</span>
            </div>
            <notification-detail :notification="topNotification"/>
          </div>

          <!-- Recent notifications -->
          <div v-if="currentEcosystemNotifications.length > 0">
            <h2 class="text-lg font-semibold text-gray-900 mb-4">Recent Notifications</h2>
            <div class="space-y-3">
              <notification-detail
                v-for="notification in currentEcosystemNotifications"
                :key="notification.id"
                :notification="notification"
              />
            </div>

            <!-- Loading state -->
            <div v-if="fetching" class="tw-flex justify-center py-8">
              <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-community"></div>
            </div>

            <!-- End of feed message -->
            <div v-else-if="endOfFeed && currentEcosystemNotifications.length > 0" class="text-center py-8">
              <p class="text-gray-500">You've reached the end of your notifications</p>
            </div>
          </div>

          <!-- Empty state -->
          <div v-else-if="!fetching" class="text-center py-12">
            <div class="w-16 h-16 mx-auto mb-4 text-gray-300">
              <svg fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z"
                      clip-rule="evenodd"/>
              </svg>
            </div>
            <h3 class="text-lg font-medium text-gray-900 mb-2">No notifications</h3>
            <p class="text-gray-500">When you have new notifications, they'll appear here.</p>
          </div>
        </div>
      </div>

      <!-- Notification Settings Panel -->
      <div
        v-if="showSettingsPanel"
        class="w-1/2 bg-white border-l border-gray-200 overflow-y-auto transition-all duration-300"
      >
        <div class="p-6 tw-flex flex-col items-center">
          <div class="tw-flex flex-end justify-end mb-6 w-[550px]">
            <DsButton @click="toggleNotificationSettings" class="tw-flex flex-end text-gray-400 hover:text-gray-600" icon="x" variant="rounded-secondary" size="small"/>
          </div>
          <NotificationSettings/>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import _throttle from 'lodash/throttle'

  import ConfigMixin from '../util/ConfigMixin.js'
  import { NotificationsMixin } from '../util/NotificationsMixin.js'

  import { fetchNotification, fetchNotificationPage, readAllNotifications, readNotifications, } from '../api/notifications.js'

  import DsTextarea from '../components/Form/DsTextarea.vue'
  import Modal from '../components/Modals/Modal.vue'
  import RadioButton from '../components/Form/RadioButton.vue'
  import NotificationDetail from '../components/Notifications/NotificationDetail.vue'
  import Dropdown from '../components/Dropdown/Dropdown.vue'
  import ImageInput from '../components/Form/ImageInput.vue'
  import Badge from '../components/Badge/Badge.vue'

  import { MUTATION_TYPES as UI_MUTATION_TYPES } from '../store/modules/ui'
  import { defineComponent } from 'vue'
  import NotificationSettings from '@/components/Profile/NotificationSettings.vue'
  import UserMixin from '@/util/UserMixin'

  export default defineComponent({
    name: 'info',
    components: {
      NotificationSettings,
      DsTextarea,
      Modal,
      RadioButton,
      NotificationDetail,
      Dropdown,
      ImageInput,
      Badge,
    },
    data () {
      return {
        errors: {},
        notifications: [],
        topNotification: {},
        fetching: false,
        endOfFeed: false,
        showSettingsPanel: false,
      }
    },
    mixins: [ConfigMixin, NotificationsMixin, UserMixin],
    computed: {
      isPrivate () {
        return this.$store.getters.isPrivate
      },
      unreadNotificationsBadgeCount () {
        return this.unreadNotificationsCount
      },
      shouldShowSettings () {
        return (
          this.$store.getters.isLoggedIn &&
          (this.$store.getters.isMember || this.$store.getters.isActor || this.$store.getters.isPortfolioMember)
        )
      },
      currentEcosystemNotifications () {
        return this.notifications.filter(notification => {
          // Filter out the top notification
          if (this.topNotification && notification.id === this.topNotification.id) {
            return false
          }

          // Only show notifications from current ecosystem or notifications without ecosystem
          return true
        })
      },
    },
    methods: {
      fetch (next) {
        if (this.fetching || this.endOfFeed) {
          return
        }

        this.fetching = true

        // Fetch notifications
        var filters = {}

        if (this.$route.query && this.$route.query.messageType) {
          filters.type = this.$route.query.messageType
        }

        if (this.config.id) {
          filters.ecosystem_id = this.config.id
        }

        fetchNotificationPage(20, next ? this.notifications.length : 0, filters)
          .then(notifications => {
            var notificationsLength = notifications.length
            if (this.topNotification && this.topNotification.id) {
              notifications = notifications.filter(noti => noti.id !== this.topNotification.id)
            }

            if (next) {
              this.notifications = this.notifications.concat(notifications)
            } else {
              this.notifications = notifications
            }

            this.endOfFeed = notificationsLength < 10
          })
          .catch(() => {
            this.error = 'Oops! Something went wrong while fetching notifications.'
            this.endOfFeed = true
          })
          .then(() => {
            this.fetching = false
          })
      },
      fetchTopNotification () {
        // Fetch the top notification if specified
        this.topNotification = {}

        if (this.$route.params.id) {
          // Mark the notification as read
          readNotifications([this.$route.params.id])

          fetchNotification(this.$route.params.id).then(notification => {
            this.topNotification = notification
          })
        }
      },
      toggleNotificationSettings () {
        this.showSettingsPanel = !this.showSettingsPanel
      },
      edit (item) {
        this.$store.commit(UI_MUTATION_TYPES.SHOW_SIDE_PANEL, { component: 'edit-announcement-panel', metaData: item })
      },
      render () {
        clearTimeout(this.timeout)
        this.timeout = setTimeout(this.actualRender, 200)
      },
      actualRender () {
        if (this.$refs.columns) {
          this.columns = Math.floor(this.$refs.columns.scrollWidth / 300) || 1
        }
      },
      setReadAllNotifications () {
        // Since visitors can also see public announcements, make sure that a user is logged in before we mark the posts as "read",
        // because marking something as "read" happens on a per user basis
        if (this.isLoggedIn) {
          readAllNotifications().then(notification => {
            //by fetching the recent notifications, it will reset the unread notifications
            this.fetchRecentNotifications()
            this.fetch()
          })
        }
      },
      scroll: _throttle(function () {
        const elem = this.$refs.scrollable
        if (elem && elem.offsetHeight + elem.scrollTop > 0.95 * elem.scrollHeight - 1500) {
          this.fetch(1)
        }
      }, 1000),
    },
    mounted: function () {
      // This event comes from the announcement edit side panel once the user confirms the change
      this.$bus.on('announcementUpdated', () => {
        this.fetch()
      })

      console.log(this.$route)
      if (this.$route.query && this.$route.query.o === 'true') {
        this.toggleNotificationSettings()
      }

      this.fetchTopNotification()
      this.actualRender()
      this.$refs.scrollable.addEventListener('scroll', this.scroll, { passive: true })
      this.scroll()
    },
    beforeUnmount () {
      this.$bus.off('resize300', this.actualRender)
      this.$bus.off('announcementUpdated')

      if (this.$refs.scrollable) {
        this.$refs.scrollable.removeEventListener('scroll', this.scroll, { passive: true })
      }

      this.$store.commit(UI_MUTATION_TYPES.HIDE_SIDE_PANEL)
    },
    watch: {
      notifications () {
        this.render()
      },
    },
  })
</script>

<style scoped>
  /* Height is now handled dynamically via computed property */

  /* Custom styles for responsive design */
  @media (max-width: 768px) {
    .page-notifications .tw-flex.items-center.justify-between {
      flex-direction: column;
      align-items: flex-start;
      gap: 1rem;
    }

    .page-notifications .tw-flex.items-center.space-x-3 {
      width: 100%;
      justify-content: space-between;
      gap: 0.5rem;
    }

    .page-notifications .flex-1.tw-flex.flex-row {
      flex-direction: column;
    }

    .page-notifications .w-1\/2 {
      width: 100% !important;
      height: 50vh; /* Give each section half the viewport height on mobile */
    }

    /* Hide settings panel on mobile by default */
    .page-notifications .w-1\/2.bg-white.border-l {
      position: fixed;
      top: 0;
      right: 0;
      height: 100vh;
      z-index: 50;
      transform: translateX(100%);
      transition: transform 0.3s ease-in-out;
    }

    .page-notifications .w-1\/2.bg-white.border-l.show {
      transform: translateX(0);
    }
  }
</style>
